<template>
  <!-- D-237 (A4): a real dialog. `aria-label` rather than `aria-labelledby` because this
       overlay has no title element at all — there is nothing to point at. -->
  <div class="menu-wrap" ref="dialog" role="dialog" aria-modal="true" aria-label="Episode description" tabindex="-1">
    <button class="close-button" @click="close">
      <span class="is-sr-only">Close description</span>
      <svg aria-hidden="true" focusable="false"
           stroke="white" fill="white" stroke-width="0"
           viewBox="0 0 24 24"
           height="20px"
           width="20px"
           xmlns="http://www.w3.org/2000/svg"><path d="M6.2253 4.81108C5.83477 4.42056 5.20161 4.42056 4.81108 4.81108C4.42056 5.20161 4.42056 5.83477 4.81108 6.2253L10.5858 12L4.81114 17.7747C4.42062 18.1652 4.42062 18.7984 4.81114 19.1889C5.20167 19.5794 5.83483 19.5794 6.22535 19.1889L12 13.4142L17.7747 19.1889C18.1652 19.5794 18.7984 19.5794 19.1889 19.1889C19.5794 18.7984 19.5794 18.1652 19.1889 17.7747L13.4142 12L19.189 6.2253C19.5795 5.83477 19.5795 5.20161 19.189 4.81108C18.7985 4.42056 18.1653 4.42056 17.7748 4.81108L12 10.5858L6.2253 4.81108Z" fill="white"></path></svg>
    </button>
    <div class="menu-content">
      <!-- 🔴 D-237 (A4): `.section` is `max-height: 140px; overflow: auto` with no
           focusable child, so before this the description could not be SCROLLED BY
           KEYBOARD at all — WCAG 2.1.1. A scroll container needs to be focusable for the
           arrow keys to reach it. `role="group"` plus a label so the stop announces as
           something rather than as an unnamed region. -->
      <div class="section" tabindex="0" role="group" aria-label="Description">
          {{ description }}
      </div>
    </div>
  </div>
</template>

<script>

import { createFocusTrap } from '../../shared-components/focusTrap';

export default {
  name: 'DescriptionModal',
  // D-237 (A4). 🪤 Before A2 this overlay had ZERO focusable children, and a trap over
  // zero focusables leaves focus behind the overlay with Tab walking the background —
  // strictly worse than no trap. That is the hard dependency A4 has on A2, and it is
  // discharged: the close control is a real button and `.section` is now a focus stop.
  mixins: [createFocusTrap({
    shown() { return true; },
    close() { this.close(); },
  })],
  emits: ['close'],
  props: {
    description: String,
  },
  methods: {
    close() {
      this.$emit('close');
    },
  },
};

</script>


<style lang="scss" scoped>
// 🔴 D-233 (A2): REQUIRED, not tidiness. This component applies `.is-sr-only`, and a
// scoped style block does not inherit `_helpers.scss` from anywhere — so without this
// import the class has NO rules here and the label renders as VISIBLE TEXT. In
// `.close-button` (20x20, `display: flex`) that text also squeezed the sibling svg to
// width 0 and pushed it outside the button, i.e. the close X disappeared. MEASURED in a
// real chromium; no unit spec or visual cell could see it (see
// `test/unit/sr-only-class-is-defined.spec.js`, which now pins it at the source).
@import '../../assets/_helpers';

.vertical {
  .menu-content {
    display: flex;
    flex-direction: column;
    gap: 60px;
    line-height: 24px;
    padding: 70px 30px 30px 40px;
  }

}

.menu-wrap {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  bottom: 10px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, .8);
  backdrop-filter: blur(1rem);
  z-index: 9;
  font-family: 'Inter', sans-serif;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  height: 20px;
  width: 20px;
  cursor: pointer;
  z-index: 10;

  // D-233 / D-242 (A2). Same conversion and the same reasoning as `Controls/Menu`'s
  // close, which this file's markup is a near-copy of; see that one for the detail.
  // 🪤 The two are NOT deduplicated here. They are separate components with separate
  // scoped blocks, and folding them together is a refactor with its own review — doing
  // it inside an accessibility MR would put a structural change behind an a11y label.
  background: transparent;
  border: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
  }
}

.share-button {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: white;
  text-decoration: none;

  &:hover {
    transition: all 0.2s ease-in-out;
    color: #90bdff;
  }
}

.menu-content {
  border-radius: 4px;
  line-height: 24px;
  padding: 30px 60px 30px 40px;
  color: white;
  display: flex;
  gap: 120px;

  .share-header {
    text-transform: uppercase;
    font-size: 12px;
    color: #7e7d7d;
    margin-bottom: 20px;
    letter-spacing: 2px;
  }

  .share-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    color: white;
  }
}

.section {
  overflow: auto;
  max-height: 140px;
  height: 140px;
}

</style>
